// src/pages/admin/EditEvent.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDarkMode } from "../../context/DarkModeContext";
import { eventService } from "../../service/eventService";
import type { Event } from "../../types/event";
import EventForm from "../../components/EventForm";
import { ArrowLeft } from "lucide-react";

const EditEvent: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useDarkMode();
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadEvent();
    }
  }, [id]);

  const loadEvent = async () => {
    try {
      setIsLoading(true);
      const eventData = await eventService.getEvent(id!);
      setEvent(eventData);
    } catch (err) {
      setError("Failed to load event");
      console.error("Error loading event:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = (updatedEvent: Event) => {
    setEvent(updatedEvent);
    navigate("/dashboard/events-manager"); // Fixed: Correct route
  };

  const handleCancel = () => {
    navigate("/dashboard/events-manager"); // Fixed: Navigate back to events manager
  };

  if (isLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDark ? "bg-gray-900" : "bg-gradient-to-br from-sky-50 to-blue-50"
        }`}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className={`mt-4 ${isDark ? "text-gray-400" : "text-blue-600"}`}>
            Loading event...
          </p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDark ? "bg-gray-900" : "bg-gradient-to-br from-sky-50 to-blue-50"
        }`}
      >
        <div className="text-center">
          <p className={`text-xl ${isDark ? "text-red-400" : "text-red-600"}`}>
            {error || "Event not found"}
          </p>
          <button
            onClick={() => navigate("/dashboard/events-manager")} // Fixed: Correct route
            className={`mt-4 px-4 py-2 rounded-lg ${
              isDark
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
            }`}
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? "bg-gray-900 text-gray-100"
          : "bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900"
      }`}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate("/dashboard/events-manager")} // Fixed: Correct route
            className={`mr-4 p-2 rounded-lg transition-colors ${
              isDark
                ? "hover:bg-gray-800 text-gray-400 hover:text-white"
                : "hover:bg-blue-100 text-blue-600 hover:text-blue-800"
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1
              className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${
                isDark
                  ? "from-blue-400 to-blue-600 text-white"
                  : "from-sky-500 to-blue-600 text-transparent bg-clip-text"
              }`}
            >
              Edit Event
            </h1>
            <p className={`mt-2 ${isDark ? "text-gray-400" : "text-blue-700"}`}>
              Update event details
            </p>
          </div>
        </div>

        <EventForm event={event} onSave={handleSave} onCancel={handleCancel} />
      </div>
    </div>
  );
};

export default EditEvent;
